import {useCallback,useEffect,useState} from 'react';
import supabase from '../supabase/supabase';
import {createId,createSeedData} from '../data/seed';
import {
  insertFishingSession,
  insertFishingSessions
} from '../data/sessionRepository';
import {
  assertSessionRowsHaveIds,
  isValidSessionId
} from '../data/sessionPersistence';
import {resetDemoEnvironment,RESET_MARKER} from '../data/demoReset';
import {availableSeats} from '../utils/booking';

const TABLES={
  guides: 'guides_1789506688831',
  tripTypes: 'trip_types_1789506688831',
  sessions: 'fishing_sessions_1789506688831',
  reservations: 'reservations_1789506688831',
  adjustments: 'seat_adjustments_1789506688831',
  waitlists: 'waitlist_entries_1789506688831'
};

const SESSION_COLUMNS='id,guide_id,trip_type_id,date,start_time,capacity,status,notes,calendar_conflict_status,created_at,updated_at';

const toType=(row)=> ({
  ...row,
  duration_hours: Number(row.duration_hours),
  price_per_seat: Number(row.price_per_seat),
  deposit_per_seat: Number(row.deposit_per_seat),
  default_capacity: Number(row.default_capacity)
});

const toSession=(row,types)=> ({
  ...row,
  capacity: Number(row.capacity),
  tripType: types.find((type)=> type.id===row.trip_type_id)
});

const seedIfEmpty=async()=> {
  const result=await supabase
    .from(TABLES.guides)
    .select('id')
    .eq('id','guide_demo')
    .limit(1);

  if (result.error) throw result.error;
  if (result.data?.length) return;

  const seed=createSeedData();
  const guide=await supabase.from(TABLES.guides).insert(seed.guide);
  if (guide.error) throw guide.error;

  const types=await supabase.from(TABLES.tripTypes).insert(seed.tripTypes);
  if (types.error) throw types.error;

  await insertFishingSessions(seed.sessions,'seedIfEmpty');

  const adjustments=await supabase
    .from(TABLES.adjustments)
    .insert(seed.adjustments);

  if (adjustments.error) throw adjustments.error;
};

const loadData=async()=> {
  await seedIfEmpty();

  const [
    guide,
    types,
    sessions,
    reservations,
    adjustments,
    waitlists
  ]=await Promise.all([
    supabase.from(TABLES.guides).select('*').eq('id','guide_demo').single(),
    supabase.from(TABLES.tripTypes).select('*').eq('guide_id','guide_demo').order('name'),
    supabase.from(TABLES.sessions).select(SESSION_COLUMNS).eq('guide_id','guide_demo').order('date').order('start_time'),
    supabase.from(TABLES.reservations).select('*').order('created_at'),
    supabase.from(TABLES.adjustments).select('*').order('created_at'),
    supabase.from(TABLES.waitlists).select('*').order('created_at')
  ]);

  const failed=[guide,types,sessions,reservations,adjustments,waitlists]
    .find((result)=> result.error);

  if (failed) throw failed.error;

  assertSessionRowsHaveIds(sessions.data,'loadData');

  const tripTypes=types.data
    .filter((row)=> row.description!==RESET_MARKER)
    .map(toType);

  const visibleSessions=sessions.data
    .filter((row)=> row.notes!==RESET_MARKER)
    .map((row)=> toSession(row,tripTypes));

  return {
    guide: guide.data,
    tripTypes,
    sessions: visibleSessions,
    reservations: reservations.data.filter((row)=> row.customer_message!==RESET_MARKER),
    adjustments: adjustments.data.filter((row)=> row.reason!==RESET_MARKER),
    waitlists: waitlists.data.filter((row)=> row.status==='WAITING')
  };
};

export function useBookingStore() {
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');

  const reload=useCallback(async()=> {
    setLoading(true);
    setError('');

    try {
      setData(await loadData());
    } catch (loadError) {
      setError(loadError.message || 'Unable to load booking data.');
    } finally {
      setLoading(false);
    }
  },[]);

  useEffect(()=> {
    reload();
  },[reload]);

  const mutate=useCallback(async(operation)=> {
    setError('');

    try {
      const result=await operation();
      await reload();
      return {ok: true,result};
    } catch (mutationError) {
      setError(mutationError.message || 'Unable to save booking data.');
      return {
        ok: false,
        error: mutationError.message || 'Unable to save booking data.'
      };
    }
  },[reload]);

  const saveSession=(values)=> mutate(async()=> {
    if (isValidSessionId(values.id)) {
      const payload={
        ...values,
        guide_id: values.guide_id || 'guide_demo'
      };

      const result=await supabase
        .from(TABLES.sessions)
        .update({
          trip_type_id: payload.trip_type_id,
          date: payload.date,
          start_time: payload.start_time,
          capacity: Number(payload.capacity),
          status: payload.status || 'OPEN',
          notes: payload.notes || '',
          calendar_conflict_status: payload.calendar_conflict_status || 'NOT_CONNECTED',
          updated_at: new Date().toISOString()
        })
        .eq('id',values.id)
        .eq('guide_id',payload.guide_id);

      if (result.error) throw result.error;
      return;
    }

    const newSession={
      ...values,
      id: createId('session'),
      guide_id: 'guide_demo'
    };

    await insertFishingSession(newSession);
  });

  const actions={
    saveTripType: (values)=> mutate(async()=> {
      const payload={
        ...values,
        id: values.id || createId('type'),
        guide_id: 'guide_demo',
        duration_hours: Number(values.duration_hours),
        price_per_seat: Number(values.price_per_seat),
        deposit_per_seat: Number(values.deposit_per_seat),
        default_capacity: Number(values.default_capacity),
        updated_at: new Date().toISOString()
      };

      const result=await supabase.from(TABLES.tripTypes).upsert(payload);
      if (result.error) throw result.error;
    }),

    deactivateTripType: (id)=> mutate(async()=> {
      const result=await supabase
        .from(TABLES.tripTypes)
        .update({active: false,updated_at: new Date().toISOString()})
        .eq('id',id)
        .eq('guide_id','guide_demo');

      if (result.error) throw result.error;
    }),

    reactivateTripType: (id)=> mutate(async()=> {
      const result=await supabase
        .from(TABLES.tripTypes)
        .update({active: true,updated_at: new Date().toISOString()})
        .eq('id',id)
        .eq('guide_id','guide_demo');

      if (result.error) throw result.error;
    }),

    saveSession,

    setSessionStatus: (id,status)=> mutate(async()=> {
      if (!isValidSessionId(id)) {
        throw new Error('Session status update was blocked because the session id was missing.');
      }

      const result=await supabase
        .from(TABLES.sessions)
        .update({status,updated_at: new Date().toISOString()})
        .eq('id',id)
        .eq('guide_id','guide_demo');

      if (result.error) throw result.error;
    }),

    adjustSeat: (sessionId,quantity,reason)=> mutate(async()=> {
      if (!isValidSessionId(sessionId)) {
        throw new Error('Inventory adjustment was blocked because the session id was missing.');
      }

      const result=await supabase
        .from(TABLES.adjustments)
        .insert({
          id: createId('adjustment'),
          trip_session_id: sessionId,
          quantity: Number(quantity),
          reason,
          created_at: new Date().toISOString()
        });

      if (result.error) throw result.error;
    }),

    createManualReservation: (values)=> mutate(async()=> {
      const sessionResult=await supabase
        .from(TABLES.sessions)
        .select(SESSION_COLUMNS)
        .eq('id',values.trip_session_id)
        .eq('guide_id','guide_demo')
        .single();

      if (sessionResult.error) throw sessionResult.error;
      assertSessionRowsHaveIds([sessionResult.data],'createManualReservation');

      const session=sessionResult.data;
      const [reservations,adjustments,type]=await Promise.all([
        supabase.from(TABLES.reservations).select('party_size,status').eq('trip_session_id',session.id),
        supabase.from(TABLES.adjustments).select('quantity').eq('trip_session_id',session.id),
        supabase.from(TABLES.tripTypes).select('*').eq('id',session.trip_type_id).single()
      ]);

      if (reservations.error) throw reservations.error;
      if (adjustments.error) throw adjustments.error;
      if (type.error) throw type.error;

      const seats=availableSeats(session,reservations.data || [],adjustments.data || []);
      const partySize=Number(values.party_size);
      const amount=Number(values.amount_received);
      const total=partySize * Number(type.data.price_per_seat);

      if (session.status!=='OPEN' || partySize<1 || partySize>seats) {
        throw new Error('Those seats are no longer available.');
      }

      if (!Number.isFinite(amount) || amount<0 || amount>total) {
        throw new Error('Amount received must be between zero and the trip total.');
      }

      const now=new Date().toISOString();
      const result=await supabase.from(TABLES.reservations).insert({
        id: createId('reservation'),
        reservation_number: `SOS-${Date.now()}`,
        trip_session_id: session.id,
        party_size: partySize,
        customer_name: values.customer_name,
        customer_email: values.customer_email,
        customer_phone: values.customer_phone,
        customer_message: '',
        price_per_seat: type.data.price_per_seat,
        deposit_per_seat: type.data.deposit_per_seat,
        trip_total: total,
        deposit_due: Number(type.data.deposit_per_seat) * partySize,
        deposit_paid: amount,
        balance_remaining: Math.max(0,total - amount),
        payment_date: amount>0 ? now : null,
        payment_status: 'SIMULATED',
        marketing_opt_in: false,
        discount_amount: 0,
        discount_eligible: false,
        status: 'CONFIRMED',
        created_at: now,
        confirmed_at: now
      });

      if (result.error) throw result.error;
    }),

    createReservation: (values)=> mutate(async()=> {
      const result=await supabase.from(TABLES.reservations).insert(values);
      if (result.error) throw result.error;
    }),

    addWaitlistEntry: (values)=> mutate(async()=> {
      const result=await supabase.from(TABLES.waitlists).insert({
        ...values,
        id: createId('waitlist'),
        party_size: Number(values.party_size),
        status: 'WAITING',
        created_at: new Date().toISOString()
      });

      if (result.error) throw result.error;
    }),

    removeWaitlistEntry: (id)=> mutate(async()=> {
      const result=await supabase
        .from(TABLES.waitlists)
        .update({status: 'REMOVED',handled_at: new Date().toISOString()})
        .eq('id',id);

      if (result.error) throw result.error;
    }),

    cancelReservation: (id)=> mutate(async()=> {
      const result=await supabase
        .from(TABLES.reservations)
        .update({status: 'CANCELLED',cancelled_at: new Date().toISOString()})
        .eq('id',id)
        .eq('status','CONFIRMED');

      if (result.error) throw result.error;
    }),

    resetDemo: ()=> mutate(async()=> {
      await resetDemoEnvironment();
    })
  };

  return {data,actions,loading,error,reload};
}